import React, { useState, useEffect } from 'react';
import './App.css';
import { SizeVisualization } from './components/SizeVisualization';
import { StandardSizes } from './components/StandardSizes';
import { ConverterPanel } from './components/ConverterPanel';
import { 
  ISO_A_SIZES, 
  ISO_B_SIZES,
  ISO_C_SIZES,
  US_SIZES,
  ANSI_SIZES,
  ARCH_SIZES,
  JIS_SIZES,
  US_ENVELOPE_SIZES,
  PHOTO_SIZES,
  COMMON_REFERENCE_OBJECTS 
} from './data/standardSizes';

function App() {
  // Main application state
  const [dimensions, setDimensions] = useState({
    width: {
      mm: '',
      inches: '',
      points: '',
      picas: '',
      pixels: ''
    },
    height: {
      mm: '',
      inches: '',
      points: '',
      picas: '',
      pixels: ''
    }
  });
  
  // Resolution settings
  const [ppi, setPpi] = useState(300);
  const [dpi, setDpi] = useState(300);
  const [customPPI, setCustomPPI] = useState(300);
  const [usesCustomPPI, setUsesCustomPPI] = useState(false);
  
  // UI state
  const [expandedPanels, setExpandedPanels] = useState({
    iso: true,
    isob: false,
    isoc: false,
    us: false,
    ansi: false,
    arch: false,
    jis: false,
    usenv: false,
    photo: false,
    refobj: false
  });
  
  // Display preferences
  const [displayUnits, setDisplayUnits] = useState('mm'); // 'mm' or 'inches'
  
  // Visualization state
  const [selectedSizes, setSelectedSizes] = useState({
    'iso-a4': true  // Start with A4 selected by default
  });
  const [showGrid, setShowGrid] = useState(true);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [selectedReferenceObjects, setSelectedReferenceObjects] = useState([]);
  const [autoZoomTrigger, setAutoZoomTrigger] = useState(null);
  const [autoZoomMode, setAutoZoomMode] = useState('all'); // 'all' or 'last' or 'none'
  
  // Toggle panel visibility (for collapsible sections)
  const togglePanel = (panel) => {
    setExpandedPanels(prev => ({
      ...prev,
      [panel]: !prev[panel]
    }));
  };
  
  const toggleSize = (sizeId) => {
    // Use functional update pattern to avoid closure issues
    setSelectedSizes(prevSelectedSizes => {
      const newSelectedSizes = {
        ...prevSelectedSizes,
        [sizeId]: !prevSelectedSizes[sizeId]
      };
      
      // Trigger auto-zoom if the display is set to zoom to last selection
      if (autoZoomMode === 'last' && newSelectedSizes[sizeId]) {
        setTimeout(() => {
          setAutoZoomTrigger({
            id: sizeId,
            timestamp: Date.now()
          });
        }, 0);
      }
      
      return newSelectedSizes;
    });
  };
  
  // Handle zoom controls
  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev * 1.2, 5));
  };
  
  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev / 1.2, 0.2));
  };
  
  const handleZoomFit = () => {
    // Trigger auto zoom
    setAutoZoomTrigger({
      timestamp: Date.now()
    });
  };
  
  // Toggle auto zoom mode between 'all', 'last', and 'none'
  const toggleAutoZoomMode = () => {
    setAutoZoomMode(prev => {
      if (prev === 'all') return 'last';
      if (prev === 'last') return 'none';
      return 'all';
    });
  };
  
  // Toggle display units
  const toggleDisplayUnits = () => {
    setDisplayUnits(prev => prev === 'mm' ? 'inches' : 'mm');
  };
  
  // Toggle reference object display
  const toggleReferenceObject = (objectId) => {
    setSelectedReferenceObjects(prev => {
      const newSelection = prev.includes(objectId)
        ? prev.filter(id => id !== objectId)
        : [...prev, objectId];
        
      // Trigger auto-zoom after state update
      if (autoZoomMode === 'last' && !prev.includes(objectId)) {
        setTimeout(() => {
          setAutoZoomTrigger({
            id: objectId,
            timestamp: Date.now()
          });
        }, 0);
      }
      
      return newSelection;
    });
  };
  
  // Toggle all sizes in a section
  const toggleAllSizes = (standardId, value) => {
    let sizesToToggle = [];
    console.log(`Toggling all sizes for standard: ${standardId} to ${value}`);
    
    switch(standardId) {
      case 'iso':
        sizesToToggle = ISO_A_SIZES.map(size => `iso-${size.id}`);
        break;
      case 'isob':
        sizesToToggle = ISO_B_SIZES.map(size => `isob-${size.id}`);
        break;
      case 'isoc':
        sizesToToggle = ISO_C_SIZES.map(size => `isoc-${size.id}`);
        break;
      case 'us':
        sizesToToggle = US_SIZES.map(size => `us-${size.id}`);
        break;
      case 'ansi':
        sizesToToggle = ANSI_SIZES.map(size => `ansi-${size.id}`);
        break;
      case 'arch':
        sizesToToggle = ARCH_SIZES.map(size => `arch-${size.id}`);
        break;
      case 'jis':
        sizesToToggle = JIS_SIZES.map(size => `jis-${size.id}`);
        break;
      case 'usenv':
        sizesToToggle = US_ENVELOPE_SIZES.map(size => `usenv-${size.id}`);
        break;
      case 'photo':
        sizesToToggle = PHOTO_SIZES.map(size => `photo-${size.id}`);
        break;
      default:
        break;
    }
    
    console.log(`Sizes to toggle: ${sizesToToggle.join(', ')}`);
    
    // Use functional update pattern
    setSelectedSizes(prevSelectedSizes => {
      // Check if all sizes in this category are already selected
      const allSelected = sizesToToggle.every(sizeId => prevSelectedSizes[sizeId]);
      
      // If value is true but all are already selected, we want to turn them all off
      // This provides toggle-like behavior for the "Select all" checkbox
      const newValue = value && allSelected ? false : value;
      
      console.log(`Toggle all in ${standardId}: setting to ${newValue} (allSelected=${allSelected})`);
      
      const newSelectedSizes = { ...prevSelectedSizes };
      sizesToToggle.forEach(sizeId => {
        newSelectedSizes[sizeId] = newValue;
      });
      
      // Trigger auto-zoom if any sizes were selected
      if (newValue && sizesToToggle.length > 0 && autoZoomMode !== 'none') {
        setTimeout(() => {
          setAutoZoomTrigger({
            timestamp: Date.now()
          });
        }, 0);
      }
      
      return newSelectedSizes;
    });
  };
  
  // Toggle all reference objects
  const toggleAllReferenceObjects = (value) => {
    setSelectedReferenceObjects(prev => {
      // Check if all objects are already selected
      const allSelected = prev.length === COMMON_REFERENCE_OBJECTS.length;
      
      // Toggle off if all are already selected and we're trying to select all
      if (value && allSelected) {
        return [];
      }
      
      const newSelection = value 
        ? COMMON_REFERENCE_OBJECTS.map(obj => obj.id)
        : [];
      
      // Trigger auto-zoom after state update
      if (value && COMMON_REFERENCE_OBJECTS.length > 0 && autoZoomMode !== 'none') {
        setTimeout(() => {
          setAutoZoomTrigger({
            timestamp: Date.now()
          });
        }, 0);
      }
      
      return newSelection;
    });
  };
  
  // Update document title to reflect tool name
  useEffect(() => {
    document.title = "PrintAxis Measurement Calculator";
  }, []);
  
  // Visualizer Panel Component
  const VisualizerPanel = () => {
    return (
      <div className="w-full h-full overflow-hidden flex flex-col bg-white">
        <div className="p-3 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
          <h2 className="text-sm font-semibold text-gray-800">Size Visualization</h2>
          <div className="flex items-center space-x-2">
            <button 
              className="p-1 rounded-sm hover:bg-gray-200"
              onClick={handleZoomOut}
            >
              <span className="text-xs">-</span>
            </button>
            <span className="text-xs">{Math.round(zoomLevel * 100)}%</span>
            <button 
              className="p-1 rounded-sm hover:bg-gray-200"
              onClick={handleZoomIn}
            >
              <span className="text-xs">+</span>
            </button>
            <button 
              className="p-1 rounded-sm hover:bg-gray-200"
              onClick={handleZoomFit}
            >
              <span className="text-xs">⤧</span>
            </button>
          </div>
        </div>
        
        {/* Visualization Controls */}
        <div className="border-b border-gray-200 p-2 bg-gray-50">
          <div className="flex flex-wrap justify-between items-center gap-2">
            <div className="flex items-center">
              <input 
                type="checkbox" 
                id="showGrid" 
                className="h-3 w-3 mr-1" 
                checked={showGrid}
                onChange={() => setShowGrid(!showGrid)}
              />
              <label htmlFor="showGrid" className="text-xs text-gray-700">Show Grid</label>
            </div>
            
            {/* Unit Toggle */}
            <div className="flex items-center bg-white rounded border border-gray-300 p-0.5">
              <button
                className={`px-2 py-1 text-xs rounded ${displayUnits === 'mm' ? 'bg-blue-600 text-white' : 'text-gray-700'}`}
                onClick={() => setDisplayUnits('mm')}
              >
                mm
              </button>
              <button
                className={`px-2 py-1 text-xs rounded ${displayUnits === 'inches' ? 'bg-blue-600 text-white' : 'text-gray-700'}`}
                onClick={() => setDisplayUnits('inches')}
              >
                inches
              </button>
            </div>
            
            {/* Auto-Zoom Toggle */}
            <div className="flex items-center bg-white rounded border border-gray-300 p-0.5">
              <button
                className={`px-2 py-1 text-xs rounded ${autoZoomMode === 'all' ? 'bg-blue-600 text-white' : 'text-gray-700'}`}
                onClick={() => setAutoZoomMode('all')}
              >
                Fit All
              </button>
              <button
                className={`px-2 py-1 text-xs rounded ${autoZoomMode === 'last' ? 'bg-blue-600 text-white' : 'text-gray-700'}`}
                onClick={() => setAutoZoomMode('last')}
              >
                Fit Last
              </button>
              <button
                className={`px-2 py-1 text-xs rounded ${autoZoomMode === 'none' ? 'bg-blue-600 text-white' : 'text-gray-700'}`}
                onClick={() => setAutoZoomMode('none')}
              >
                Manual
              </button>
            </div>
          </div>
        </div>
        
        {/* Visualization Canvas with 1.414:1 aspect ratio */}
        <div className="flex-1 relative bg-gray-100 overflow-auto" style={{ aspectRatio: '0.707' }}>
          <SizeVisualization 
            dimensions={dimensions}
            selectedSizes={selectedSizes}
            showGrid={showGrid}
            zoomLevel={zoomLevel}
            setZoomLevel={setZoomLevel}
            selectedReferenceObjects={selectedReferenceObjects}
            autoZoom={autoZoomTrigger}
            autoZoomMode={autoZoomMode}
            displayUnits={displayUnits}
            aspectRatio={1.414}
          />
        </div>
        
        {/* Legend */}
        <div className="border-t border-gray-200 p-2 bg-gray-50">
          <div className="flex flex-wrap gap-2">
            {/* Only show Your Size legend if custom dimensions are present */}
            {dimensions.width.mm && dimensions.height.mm && 
              !isNaN(parseFloat(dimensions.width.mm)) && 
              !isNaN(parseFloat(dimensions.height.mm)) && (
              <div className="flex items-center">
                <div className="w-3 h-3 bg-emerald-500 mr-1"></div>
                <span className="text-xs text-gray-700">Your Size</span>
              </div>
            )}
            
            {/* ISO Series (consolidated) */}
            {Object.entries(selectedSizes).some(([id, selected]) => 
              selected && (id.startsWith('iso-') || id.startsWith('isob-') || id.startsWith('isoc-'))
            ) && (
              <div className="flex items-center">
                <div className="w-3 h-3 bg-blue-500 mr-1"></div>
                <span className="text-xs text-gray-700">ISO Series</span>
              </div>
            )}
            
            {/* US Paper Sizes */}
            {Object.entries(selectedSizes).some(([id, selected]) => selected && id.startsWith('us-')) && (
              <div className="flex items-center">
                <div className="w-3 h-3 bg-amber-500 mr-1"></div>
                <span className="text-xs text-gray-700">US Paper</span>
              </div>
            )}
            
            {/* ANSI Series */}
            {Object.entries(selectedSizes).some(([id, selected]) => selected && id.startsWith('ansi-')) && (
              <div className="flex items-center">
                <div className="w-3 h-3 bg-violet-600 mr-1"></div>
                <span className="text-xs text-gray-700">ANSI Series</span>
              </div>
            )}
            
            {/* ARCH Series */}
            {Object.entries(selectedSizes).some(([id, selected]) => selected && id.startsWith('arch-')) && (
              <div className="flex items-center">
                <div className="w-3 h-3 bg-orange-500 mr-1"></div>
                <span className="text-xs text-gray-700">ARCH Series</span>
              </div>
            )}
            
            {/* JIS Series */}
            {Object.entries(selectedSizes).some(([id, selected]) => selected && id.startsWith('jis-')) && (
              <div className="flex items-center">
                <div className="w-3 h-3 bg-fuchsia-500 mr-1"></div>
                <span className="text-xs text-gray-700">JIS Series</span>
              </div>
            )}
            
            {/* US Envelope Sizes */}
            {Object.entries(selectedSizes).some(([id, selected]) => selected && id.startsWith('usenv-')) && (
              <div className="flex items-center">
                <div className="w-3 h-3 bg-amber-500 mr-1"></div>
                <span className="text-xs text-gray-700">US Envelopes</span>
              </div>
            )}
            
            {/* Photo Print Sizes */}
            {Object.entries(selectedSizes).some(([id, selected]) => selected && id.startsWith('photo-')) && (
              <div className="flex items-center">
                <div className="w-3 h-3 bg-rose-500 mr-1"></div>
                <span className="text-xs text-gray-700">Photo Sizes</span>
              </div>
            )}
            
            {/* Reference Objects */}
            {selectedReferenceObjects.length > 0 && (
              <div className="flex items-center">
                <div className="w-3 h-3 bg-gray-500 mr-1"></div>
                <span className="text-xs text-gray-700">Reference Objects</span>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };
  
  return (
    <div className="w-full h-screen flex flex-col bg-gray-100">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 p-3">
        <h1 className="text-lg font-bold text-gray-800">PrintAxis</h1>
        <p className="text-sm text-gray-600">Measurement Calculator</p>
      </header>
      
      {/* Main Layout */}
      <div className="flex-1 flex overflow-hidden" style={{ maxHeight: 'calc(100vh - 82px)' }}>
        {/* Left Column - Standard Sizes Sidebar (1/6) */}
        <div className="w-1/6 max-w-xs">
          <div className="w-full h-full overflow-y-auto bg-gray-50 border-r border-gray-200">
            <div className="p-3 border-b border-gray-200 bg-white">
              <h2 className="text-sm font-semibold text-gray-800">Standard Sizes & Objects</h2>
            </div>
            <div className="p-2">
              <StandardSizes
                panelStates={expandedPanels}
                togglePanel={togglePanel}
                selectedSizes={selectedSizes}
                toggleSize={toggleSize}
                handleAutoZoom={size => handleZoomFit()}
                toggleAllInStandard={toggleAllSizes}
                selectedReferenceObjects={selectedReferenceObjects}
                toggleReferenceObject={toggleReferenceObject}
                toggleAllReferenceObjects={toggleAllReferenceObjects}
                compact={true}
              />
            </div>
          </div>
        </div>
        
        {/* Middle Column - Converter (1/3) */}
        <div className="w-1/3">
          <ConverterPanel
            dimensions={dimensions}
            setDimensions={setDimensions}
            ppi={ppi}
            dpi={dpi}
            setPpi={setPpi}
            setDpi={setDpi}
            usesCustomPPI={usesCustomPPI}
            setUsesCustomPPI={setUsesCustomPPI}
            customPPI={customPPI}
            setCustomPPI={setCustomPPI}
          />
        </div>
        
        {/* Right Column - Visualizer (flex-1) */}
        <div className="flex-1">
          <VisualizerPanel />
        </div>
      </div>
      
      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 p-2 text-center">
        <p className="text-xs text-gray-600">PrintAxis Measurement Calculator | PrintPraxis Tools</p>
      </footer>
    </div>
  );
}

export default App;